/**
 * 저장된 RRULE 문자열을 수정 모달의 반복 입력 상태로 되돌린다 (2026-09-14).
 *
 * 수정 모달에서 반복 설정을 바꾸려면 먼저 **지금 설정을 화면에 채워** 줘야 한다.
 * 서버가 주는 것은 `FREQ=WEEKLY;INTERVAL=2;BYDAY=MO,WE;COUNT=10` 같은 한 줄뿐이라
 * 여기서 탭/간격/요일/종료조건으로 되풀어 준다. 만드는 쪽(buildRecurrenceRule)이 내는
 * 조합만 다루면 되므로 RRULE 전체 문법을 구현하지 않는다 — 못 읽으면 null 을 준다.
 */
import { shiftWeekday } from './recurrence-dates';

export type RecurFreq = '' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
/**
 * ⭐ 2026-10-14 (반복 규칙 E-3 · 사용자 확정) — 'period'(기간 「N주 동안」)를 더했다. 저장은 종료일(UNTIL)로 간다
 *   (recurrence-form `periodEndDate`). 'count'(횟수)는 이미 COUNT 로 저장된 일정을 열 때만 나온다 — 새로 고르는 선택지에는 없다.
 */
export type RecurEnd = 'never' | 'count' | 'until' | 'period';

export interface ParsedRecurrence {
  freq: Exclude<RecurFreq, ''>;
  interval: number;
  /** WEEKLY 의 요일 체크박스. MONTHLY/YEARLY 의 BYDAY 는 byWeekday 로 접힌다. */
  byDay: string[];
  /** "해당 주+요일"(BYSETPOS) 모드인지 — 매월 셋째 화요일 같은 것. */
  byWeekday: boolean;
  end: RecurEnd;
  count: number;
  /** `<input type="date">` 가 쓰는 현지 날짜(YYYY-MM-DD). end 가 'until' 일 때만 채운다. */
  until: string;
}

const FREQS = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'] as const;

/** `20261231T145959Z` / `20261231` → 현지 기준 `YYYY-MM-DD`. */
function untilToLocalDate(raw: string): string {
  const m = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})Z?)?$/.exec(raw.trim());
  if (!m) return '';
  const [, y, mo, d, hh, mi, ss] = m;
  // 시각이 없으면 날짜 그대로 쓴다(시간대 보정 대상이 아니다).
  if (!hh) return `${y}-${mo}-${d}`;
  const at = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(hh), Number(mi), Number(ss)));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

/**
 * ⭐ 2026-10-13 — `startIso`(그 일정의 시작 순간)를 주면 매주 요일을 «현지 요일»로 되돌린다.
 *   서버는 BYDAY 를 «UTC 요일»로 적는다(`rewriteBydayFromDtstart` · `localizedRuleTokens`) — KST 00:00~08:59
 *   일정은 UTC 로 전날이라 「월」이 `SU` 로 저장된다. 안 되돌리면 수정 화면이 「일」에 체크된 채 열린다.
 *   ⚠ 안 주면 종전 그대로(글자 그대로 읽는다).
 */
const WEEKDAY_ORDER = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

/** 그 순간의 «UTC 날짜 − 현지 날짜»(일) — KST 00:00~08:59 는 1. 못 읽으면 0. (「이후 일정」 미리보기도 쓴다.) */
export function utcMinusLocalDays(startIso: string | undefined): number {
  if (!startIso) return 0;
  const s = new Date(startIso);
  if (Number.isNaN(s.getTime())) return 0;
  const utc = Date.UTC(s.getUTCFullYear(), s.getUTCMonth(), s.getUTCDate());
  const local = Date.UTC(s.getFullYear(), s.getMonth(), s.getDate());
  return Math.round((utc - local) / 86_400_000);
}

/** RRULE 한 줄 → 「이름(대문자) → 값」. `RRULE:` 머리는 떼고, `=` 없는 조각은 버린다. (「이후 일정」 미리보기도 쓴다.) */
export function parseRuleTokens(rule: string): Map<string, string> {
  const parts = new Map<string, string>();
  for (const chunk of rule.replace(/^RRULE:/i, '').split(';')) {
    const idx = chunk.indexOf('=');
    if (idx > 0) parts.set(chunk.slice(0, idx).trim().toUpperCase(), chunk.slice(idx + 1).trim());
  }
  return parts;
}

export function parseRecurrenceRule(rule: string | null | undefined, startIso?: string): ParsedRecurrence | null {
  if (!rule) return null;
  const parts = parseRuleTokens(rule);
  const freq = (parts.get('FREQ') ?? '').toUpperCase() as (typeof FREQS)[number];
  if (!FREQS.includes(freq)) return null;

  const interval = Math.max(1, Number(parts.get('INTERVAL') ?? 1) || 1);
  const byday = (parts.get('BYDAY') ?? '').split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
  // 2026-09-15 — "말일로 당기기" 날짜 규칙(BYMONTHDAY=31,-1;BYSETPOS=1 등)도 BYSETPOS 를 쓴다.
  // 요일 방식(몇째 주 무슨 요일)은 BYDAY 가 함께 있을 때만이다.
  // ⭐ 2026-10-13 — KST 새벽의 「몇째 주 요일」은 서버가 `BYDAY=…;BYMONTHDAY=7,8,…` 꼴로 적는다
  //   (BYSETPOS 없이 «날짜 창»으로 — chat-server `localizedRuleTokens` 표). 그것도 요일 방식이다.
  const hasSetpos = (parts.has('BYSETPOS') || parts.has('BYMONTHDAY')) && byday.length > 0;
  const delta = utcMinusLocalDays(startIso);
  const localByday = delta === 0
    ? byday
    // 모르는 글자는 그대로 둔다(종전과 같다).
    : byday.map((c) => shiftWeekday(WEEKDAY_ORDER, c, -delta) ?? c);

  const countRaw = Number(parts.get('COUNT'));
  const untilRaw = parts.get('UNTIL');
  let end: RecurEnd = 'never';
  if (Number.isFinite(countRaw) && countRaw > 0) end = 'count';
  else if (untilRaw) end = 'until';

  return {
    freq,
    interval,
    // BYSETPOS 가 붙은 BYDAY 는 요일 체크박스가 아니라 "몇째 주 무슨 요일" 모드다.
    byDay: freq === 'WEEKLY' && !hasSetpos ? localByday : [],
    byWeekday: (freq === 'MONTHLY' || freq === 'YEARLY') && hasSetpos,
    end,
    count: end === 'count' ? countRaw : 10,
    until: end === 'until' ? untilToLocalDate(untilRaw!) : '',
  };
}

/**
 * 화면의 반복 입력이 처음 상태에서 **바뀌었는지** 한 줄로 비교하기 위한 지문.
 * 서버에 보낼 값과 같은 축(탭·간격·요일·종료조건)만 본다 — 화면에 안 보이는 차이는 무시.
 */
export function recurrenceSignature(v: {
  freq: RecurFreq;
  interval: number;
  byDay: Iterable<string>;
  byWeekday: boolean;
  end: RecurEnd;
  count: number;
  until: string;
  /** ⭐ 2026-10-14 (E-3) — end 가 'period' 일 때의 N. */
  period?: number;
}): string {
  if (!v.freq) return 'none';
  const days = v.freq === 'WEEKLY' && !v.byWeekday ? [...v.byDay].sort().join(',') : '';
  const weekly = (v.freq === 'MONTHLY' || v.freq === 'YEARLY') && v.byWeekday ? '1' : '0';
  const tail = v.end === 'count' ? `c${v.count}` : v.end === 'until' ? `u${v.until}` : v.end === 'period' ? `p${v.period ?? 0}` : 'never';
  return [v.freq, v.interval, days, weekly, tail].join('|');
}
